// src/pages/UserManagement.js
// ✅ Full user management: View, Suspend, Reactivate, Delete
// Delete triggers Cloud Function onAccountDeletionRequested
// which purges ALL data automatically
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, updateDoc,
  addDoc, serverTimestamp, query, where
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import Paginator from '../components/Paginator';

const PAGE_SIZE = 25;

const FUNCTIONS_BASE = 'https://us-central1-crewboard-92511.cloudfunctions.net';

// ─── Delete Confirmation Modal ───────────────────────────────────────────────
const DeleteModal = ({ user, onConfirm, onCancel, loading }) => {
  const [typed, setTyped] = useState('');
  const isConfirmed = typed === user?.fullName;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid #EF4444', borderRadius: 16, padding: 32, width: '100%', maxWidth: 480 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🗑️</div>
          <div>
            <h3 style={{ fontFamily: 'Syne', color: '#EF4444', margin: 0 }}>Delete Account</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: 0 }}>This action cannot be undone</p>
          </div>
        </div>

        {/* User Info */}
        <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <div style={{ color: 'var(--text)', fontWeight: 700 }}>{user?.fullName}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>{user?.email}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>Role: {user?.role} · Plan: {user?.planId || 'free'}</div>
        </div>

        {/* What will be deleted */}
        <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <div style={{ color: '#EF4444', fontWeight: 600, fontSize: 13, marginBottom: 10 }}>☁️ Cloud Function will automatically delete:</div>
          {[
            '🗂️ All Firestore documents (profile, jobs, applications, reviews)',
            '🔐 Firebase Auth account',
            '📸 CDL license photos from Storage',
            '📧 PIPEDA confirmation email sent to user',
          ].map((item, i) => (
            <div key={i} style={{ color: 'var(--text-secondary)', fontSize: 12, padding: '4px 0', borderBottom: i < 3 ? '1px solid rgba(239,68,68,0.1)' : 'none' }}>
              {item}
            </div>
          ))}
        </div>

        {/* Type to confirm */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
            Type <span style={{ color: 'var(--text)' }}>{user?.fullName}</span> to confirm
          </label>
          <input
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder={`Type "${user?.fullName}" here`}
            style={{
              width: '100%', background: 'var(--surface2)',
              border: `1.5px solid ${isConfirmed ? '#EF4444' : 'var(--border)'}`,
              borderRadius: 10, padding: '12px 16px', color: 'var(--text)',
              fontSize: 14, outline: 'none', fontFamily: 'DM Sans',
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{ flex: 1, background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 8, padding: '12px', cursor: 'pointer', fontSize: 14 }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!isConfirmed || loading}
            style={{
              flex: 1, background: isConfirmed ? '#EF4444' : 'rgba(239,68,68,0.10)',
              border: `1px solid ${isConfirmed ? '#EF4444' : 'rgba(239,68,68,0.25)'}`,
              color: isConfirmed ? '#fff' : 'var(--text-muted)',
              borderRadius: 8, padding: '12px', cursor: isConfirmed ? 'pointer' : 'not-allowed',
              fontSize: 14, fontWeight: 700, transition: 'all 0.2s',
            }}
          >
            {loading ? 'Deleting...' : '🗑️ Delete Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing]   = useState(false);
  const [page, setPage] = useState(1);

  // ── One-time sync: backfills emailVerified from Auth into Firestore ─────────
  // Run this once to fix legacy users created before the emailVerified field
  // was added to signup. Shows "— Legacy" until synced.
  const handleSyncEmailVerified = async () => {
    if (!window.confirm(
      'Sync email verification status for all legacy users?\n\n' +
      'This reads each user\'s actual email verification status from Firebase Auth ' +
      'and writes it to Firestore. Run this once to fix users created before ' +
      'the emailVerified field was added.\n\nThis may take a few seconds.'
    )) return;

    setSyncing(true);
    try {
      const auth  = getAuth();
      const token = await auth.currentUser.getIdToken();
      const res   = await fetch(`${FUNCTIONS_BASE}/syncEmailVerifiedField`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ Sync complete.\n\n${data.synced} users updated, ${data.errors} errors out of ${data.total} legacy users.`);
        load(); // refresh the table
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err) {
      alert('Sync failed. Please try again.');
      console.error(err);
    } finally {
      setSyncing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setUsers(data);
      setFiltered(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let result = [...users];
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(u =>
        u.fullName?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s) ||
        u.phone?.includes(s) ||
        u.companyName?.toLowerCase().includes(s)
      );
    }
    if (roleFilter !== 'all') result = result.filter(u => u.role === roleFilter);
    if (statusFilter === 'active') result = result.filter(u => u.accountStatus !== 'suspended' && u.accountStatus !== 'pending_deletion');
    if (statusFilter === 'suspended') result = result.filter(u => u.accountStatus === 'suspended');
    if (statusFilter === 'pending_deletion') result = result.filter(u => u.accountStatus === 'pending_deletion');
    if (statusFilter === 'unverified') result = result.filter(u => u.emailVerified === false);
    setFiltered(result);
    setPage(1);
  }, [search, roleFilter, statusFilter, users]);

  // ── Suspend ──
  const handleSuspend = async (user) => {
    if (!window.confirm(`Suspend ${user.fullName}?\n\nThey will be locked out of the app. You can reactivate anytime.`)) return;
    await updateDoc(doc(db, 'users', user.id), {
      accountStatus: 'suspended',
      suspendedAt: serverTimestamp(),
      suspendedReason: 'Suspended by admin',
    });
    load();
    if (selected?.id === user.id) setSelected({ ...selected, accountStatus: 'suspended' });
  };

  // ── Reactivate / Cancel Deletion ──
  const handleReactivate = async (user) => {
    const isCancellingDeletion = user.accountStatus === 'pending_deletion';
    const confirmMsg = isCancellingDeletion
      ? `Cancel deletion for ${user.fullName}?\n\nTheir account will be reactivated and the scheduled deletion will be cancelled.`
      : `Reactivate ${user.fullName}'s account?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      // Reactivate account
      await updateDoc(doc(db, 'users', user.id), {
        accountStatus: 'active',
        suspendedReason: null,
        scheduledDeletionDate: null,
        deletionRequestedAt: null,
      });

      // If cancelling deletion — also cancel the pending dataRequest
      if (isCancellingDeletion) {
        const reqSnap = await getDocs(
          query(
            collection(db, 'dataRequests'),
            where('userId', '==', user.id),
            where('type', '==', 'deletion'),
            where('status', '==', 'pending')
          )
        );
        // Mark all pending deletion requests as cancelled
        for (const reqDoc of reqSnap.docs) {
          await updateDoc(reqDoc.ref, {
            status: 'cancelled',
            cancelledAt: serverTimestamp(),
            cancelledBy: 'admin',
          });
        }
        alert(`✅ Deletion cancelled for ${user.fullName}.\n\nTheir account has been reactivated.`);
      }

      load();
      if (selected?.id === user.id) setSelected(null);
    } catch (err) {
      alert('Error updating account. Please try again.');
      console.error(err);
    }
  };

  // ── Delete (triggers Cloud Function onAccountDeletionCreated) ──
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // Step 1: Mark user as pending_deletion so app shows suspension screen instantly
      // deletedByAdmin: true tells Cloud Function to skip the 30-day grace period email
      await updateDoc(doc(db, 'users', deleteTarget.id), {
        accountStatus: 'pending_deletion',
        deletedByAdmin: true,
        deletionRequestedAt: serverTimestamp(),
      });

      // Step 2: Create dataRequest with status=completed
      // Cloud Function onAccountDeletionCreated watches onCreate
      // and automatically purges ALL data
      await addDoc(collection(db, 'dataRequests'), {
        userId: deleteTarget.id,
        userEmail: deleteTarget.email,
        type: 'deletion',
        status: 'completed',
        requestedBy: 'admin',
        requestedAt: serverTimestamp(),
        completedAt: serverTimestamp(),
        completedBy: 'admin',
      });

      const deletedName = deleteTarget.fullName;
      setDeleteTarget(null);
      setSelected(null);
      load();

      alert(`✅ Deletion initiated for ${deletedName}.\n\nCloud Function is purging all their data automatically and will send them a PIPEDA confirmation email.`);
    } catch (err) {
      alert('Error initiating deletion. Please try again.');
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '9px 14px',
    color: 'var(--text)', fontSize: 13, outline: 'none',
  };

  const getStatusColor = (status) => {
    if (status === 'active') return { color: '#22C55E', label: '✅ Active' };
    if (status === 'suspended') return { color: '#EF4444', label: '⛔ Suspended' };
    if (status === 'pending_deletion') return { color: '#EAB308', label: '⏳ Deletion Pending' };
    return { color: 'var(--text-muted)', label: status };
  };

  return (
    <div className="animate-fade">
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>User Management</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Search, view, suspend, and delete users — {users.length} total
        </p>
      </div>

      {/* Action Guide */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
        {[
          { icon: '⛔', title: 'Suspend', color: '#EAB308', desc: 'Temporarily lock account. Data preserved. Reversible.' },
          { icon: '🔓', title: 'Reactivate / Cancel Deletion', color: '#22C55E', desc: 'Restore suspended account OR cancel a pending deletion request.' },
          { icon: '🗑️', title: 'Delete', color: '#EF4444', desc: 'Permanently remove all data. Cloud Function handles purge. Irreversible.' },
        ].map(a => (
          <div key={a.title} style={{ background: 'var(--surface)', border: `1px solid ${a.color}22`, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>{a.icon}</div>
            <div style={{ color: a.color, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{a.title}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5 }}>{a.desc}</div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search by name, email, phone, company..."
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
        />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={inputStyle}>
          <option value="all">All Roles</option>
          <option value="driver">🧑‍✈️ Drivers</option>
          <option value="hirer">🏢 Hirers</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={inputStyle}>
          <option value="all">All Status</option>
          <option value="active">✅ Active</option>
          <option value="suspended">⛔ Suspended</option>
          <option value="pending_deletion">🗑️ Pending Deletion</option>
          <option value="unverified">❌ Email Unverified</option>
        </select>
      </div>

      {/* Count + Sync button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Showing {filtered.length} of {users.length} users
          {users.filter(u => u.emailVerified === undefined).length > 0 && (
            <span style={{ color: '#EAB308', marginLeft: 10 }}>
              · {users.filter(u => u.emailVerified === undefined).length} legacy users showing "— Legacy"
            </span>
          )}
        </div>
        {users.some(u => u.emailVerified === undefined) && (
          <button
            onClick={handleSyncEmailVerified}
            disabled={syncing}
            style={{
              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
              color: '#3B82F6', borderRadius: 8, padding: '7px 14px',
              fontSize: 12, fontWeight: 600, cursor: syncing ? 'not-allowed' : 'pointer',
              opacity: syncing ? 0.6 : 1,
            }}
            title="One-time fix: sync email verification status from Firebase Auth into Firestore for legacy users"
          >
            {syncing ? '⏳ Syncing...' : '🔄 Sync Email Verification (legacy users)'}
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60 }}>Loading users...</div>
      ) : (() => {
        const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
        const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        return (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 16, overflow: 'hidden' }}>
          {/* Header row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1fr 150px', gap: 16, padding: '12px 20px', borderBottom: '1px solid var(--surface2)', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            <span>Name</span>
            <span>Email</span>
            <span>Role</span>
            <span>Email Verified</span>
            <span>CDL / Business</span>
            <span>Account</span>
            <span>Actions</span>
          </div>

          {paginated.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>No users found</div>
          ) : (
            paginated.map((user, i) => {
              const statusConf = getStatusColor(user.accountStatus);
              return (
                <div
                  key={user.id}
                  style={{
                  display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr 1fr 150px',
                    gap: 16, padding: '14px 20px', alignItems: 'center',
                    borderBottom: i < paginated.length - 1 ? '1px solid var(--surface2)' : 'none',
                    cursor: 'pointer', transition: 'background 0.15s',
                    background: selected?.id === user.id ? 'var(--surface2)' : 'transparent',
                    opacity: user.accountStatus === 'pending_deletion' ? 0.5 : 1,
                  }}
                  onClick={() => setSelected(selected?.id === user.id ? null : user)}
                  onMouseEnter={e => { if (selected?.id !== user.id) e.currentTarget.style.background = 'var(--surface)'; }}
                  onMouseLeave={e => { if (selected?.id !== user.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>{user.fullName}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                  <div>
                    <span style={{
                      background: user.role === 'driver' ? 'rgba(249,115,22,0.15)' : 'rgba(59,130,246,0.15)',
                      color: user.role === 'driver' ? '#F97316' : '#3B82F6',
                      borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600,
                    }}>{user.role}</span>
                  </div>
                  {/* Email verified badge — only show ❌ when explicitly false.
                      Legacy users don't have this field, treat as verified. */}
                  {/* Col 4: Email Verified
                      true  = verified (new signup, clicked link)
                      false = unverified (new signup, didn't click link yet)
                      undefined = legacy user created before this field existed */}
                  <div style={{ fontSize: 12 }}>
                    {user.emailVerified === true ? (
                      <span style={{ color: '#22C55E', fontWeight: 600 }}>✅ Yes</span>
                    ) : user.emailVerified === false ? (
                      <span style={{ color: '#EF4444', fontWeight: 600 }} title="Has not clicked the verification link yet">
                        ❌ No
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }} title="Created before email verification was introduced">
                        — Legacy
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    {user.role === 'driver' ? (() => {
                      const s = user.licenseStatus;
                      if (s === 'verified')     return <span style={{ color: '#22C55E', fontWeight: 600 }}>✅ CDL Verified</span>;
                      if (s === 'pending')      return <span style={{ color: '#EAB308', fontWeight: 600 }}>⏳ CDL Pending</span>;
                      if (s === 'rejected')     return <span style={{ color: '#EF4444', fontWeight: 600 }}>❌ CDL Rejected</span>;
                      if (s === 'expired')      return <span style={{ color: '#EF4444', fontWeight: 600 }}>⚠️ CDL Expired</span>;
                      return <span style={{ color: 'var(--text-muted)' }}>— CDL not uploaded</span>;
                    })() : (() => {
                      const s = user.verificationStatus;
                      if (s === 'verified')      return <span style={{ color: '#22C55E', fontWeight: 600 }}>✅ Biz Verified</span>;
                      if (s === 'pending')       return <span style={{ color: '#EAB308', fontWeight: 600 }}>⏳ Biz Pending</span>;
                      if (s === 'rejected')      return <span style={{ color: '#EF4444', fontWeight: 600 }}>❌ Biz Rejected</span>;
                      return <span style={{ color: 'var(--text-muted)' }}>— Docs not submitted</span>;
                    })()}
                  </div>
                  <div style={{ color: statusConf.color, fontSize: 12, fontWeight: 600 }}>
                    {statusConf.label}
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    {user.accountStatus === 'pending_deletion' ? (
                      // User requested deletion — show Cancel Deletion button
                      <button
                        onClick={() => handleReactivate(user)}
                        title="Cancel deletion and reactivate account"
                        style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22C55E', borderRadius: 6, padding: '5px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                      >🔄 Cancel Deletion</button>
                    ) : user.accountStatus !== 'suspended' ? (
                      <>
                        {/* Suspend only available when email is verified —
                            no point suspending a ghost account */}
                        {user.emailVerified !== false && (
                          <button
                            onClick={() => handleSuspend(user)}
                            title="Suspend account"
                            style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)', color: '#EAB308', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}
                          >⛔</button>
                        )}
                        <button
                          onClick={() => setDeleteTarget(user)}
                          title="Delete account permanently"
                          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}
                        >🗑️</button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleReactivate(user)}
                          title="Reactivate account"
                          style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#22C55E', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}
                        >🔓</button>
                        <button
                          onClick={() => setDeleteTarget(user)}
                          title="Delete account permanently"
                          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}
                        >🗑️</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        );
      })()}

      {filtered.length > PAGE_SIZE && (
        <Paginator page={page} totalPages={Math.ceil(filtered.length / PAGE_SIZE)} totalItems={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
      )}

      {/* User Detail Side Panel */}
      {selected && (
        <div style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: 360, background: 'var(--bg)', borderLeft: '1px solid var(--border)', padding: 28, overflowY: 'auto', zIndex: 100 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h3 style={{ fontFamily: 'Syne', color: 'var(--text)', fontWeight: 700, margin: 0 }}>User Details</h3>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          {/* Avatar */}
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: selected.role === 'driver' ? '#F97316' : '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne', fontWeight: 800, fontSize: 28, color: '#fff', margin: '0 auto 20px' }}>
            {selected.fullName?.charAt(0)}
          </div>

          {/* Details */}
          {[
            ['Full Name', selected.fullName],
            ['Email', selected.email],
            ['Phone', selected.phone || 'N/A'],
            ['Role', selected.role],
            ['Company', selected.companyName || 'N/A'],
            ['Location', selected.location || 'N/A'],
            ['Account Status', selected.accountStatus || 'active'],
            ['Plan', selected.planId || 'free'],
            ['Subscription', selected.subscriptionActive ? '✅ Active' : '❌ Inactive'],
            ['License Status', selected.licenseStatus || 'N/A'],
            ['License Class', selected.licenseClass || 'N/A'],
            ['License #', selected.licenseNumber || 'N/A'],
            ['License Expiry', selected.licenseExpiry || 'N/A'],
            ['Jobs Applied (mo)', selected.jobApplicationsThisMonth || 0],
            ['Avg Rating', selected.avgRating ? `${selected.avgRating}⭐` : 'N/A'],
            ['Jobs Completed', selected.jobsCompleted || 0],
            ['Review Count', selected.reviewCount || 0],
            ['Joined', selected.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--surface2)' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{k}</span>
              <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 500, textAlign: 'right', maxWidth: 200 }}>{String(v)}</span>
            </div>
          ))}

          {selected.licenseImageUrl && (
            <a href={selected.licenseImageUrl} target="_blank" rel="noreferrer"
              style={{ display: 'block', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', color: '#3B82F6', borderRadius: 8, padding: '10px', textAlign: 'center', marginTop: 20, fontWeight: 600, textDecoration: 'none' }}>
              👁 View License Photo
            </a>
          )}

          {/* Action Buttons in Panel */}
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>

            {/* Cancel Deletion — shown when user requested deletion */}
            {selected.accountStatus === 'pending_deletion' && (
              <>
                <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#EAB308', marginBottom: 4 }}>
                  ⏳ User requested account deletion. Click below to cancel and restore their account.
                </div>
                <button onClick={() => { handleReactivate(selected); setSelected(null); }} style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22C55E', borderRadius: 8, padding: '12px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                  🔄 Cancel Deletion & Reactivate
                </button>
              </>
            )}

            {/* Suspend — only for active accounts with verified email */}
            {selected.accountStatus === 'active' && selected.emailVerified !== false && (
              <button onClick={() => handleSuspend(selected)} style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', color: '#EAB308', borderRadius: 8, padding: '12px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                ⛔ Suspend Account
              </button>
            )}

            {/* Reactivate — only for suspended accounts */}
            {selected.accountStatus === 'suspended' && (
              <button onClick={() => handleReactivate(selected)} style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22C55E', borderRadius: 8, padding: '12px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                🔓 Reactivate Account
              </button>
            )}

            {/* Delete — only for active or suspended accounts */}
            {selected.accountStatus !== 'pending_deletion' && (
              <button onClick={() => { setDeleteTarget(selected); setSelected(null); }} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 8, padding: '12px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                🗑️ Delete Account Permanently
              </button>
            )}
          </div>

          <p style={{ color: 'var(--text-muted)', fontSize: 11, textAlign: 'center', marginTop: 16, lineHeight: 1.5 }}>
            Deletion triggers Cloud Function to purge all data automatically
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <DeleteModal
          user={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}